import { FC, forwardRef, useMemo } from 'react';

import { RecipientPopover } from '@bonusly/common-recipient-popover';
import { Avatar, AvatarSize, Box, BoxProps, Flex, rem, StyleProps } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

import { ProfileLink } from '../../../../../modules/profile/components/profile-link';
import { GroupAvatar, isGroupAvatarType } from '../../../../groups/group-avatar';
import { UserAvatarExcess } from '../../components/user-avatar/user-avatar-excess';
import { UserBonusFeedProps } from '../../types';
import { pluck } from '../../utils';
import { splitOverflow } from '../../utils/array';

/**
 * A wrapper component that makes the avatar accessible as a popover trigger.
 * Uses a button element to properly support aria-expanded attribute.
 */
const AvatarTriggerWrapper = forwardRef<HTMLButtonElement, BoxProps & { children: React.ReactNode }>(({ children, ...rest }, ref) => (
	<Box
		ref={ref}
		as="button"
		type="button"
		display="inline-flex"
		alignItems="center"
		bg="transparent"
		border="none"
		p={0}
		cursor="pointer"
		lineHeight={0}
		{...rest}
	>
		{children}
	</Box>
));

AvatarTriggerWrapper.displayName = 'AvatarTriggerWrapper';

export const DEFAULT_MAX_USERS = 5;

// Define a mapping between our component sizes and Avatar component sizes
const sizeMap: Record<'lg' | 'xl' | '2xl', AvatarSize> = {
	lg: 'md',
	xl: 'lg',
	'2xl': '2xl',
};

type AvatarGroupSize = 'lg' | 'xl' | '2xl';

interface UserAvatarGroupProps extends StyleProps {
	isPopoverDisabled?: boolean;
	max?: number;
	onAvatarClick?: (event: any) => void | VoidFunction;
	size?: AvatarGroupSize;
	users: UserBonusFeedProps[];
	recipients?: Recipient[];
}

const getMarginProps = () => {
	return {
		marginEnd: rem(-8),
	};
};

export const UserAvatarGroup: FC<UserAvatarGroupProps> = ({
	isPopoverDisabled,
	max = DEFAULT_MAX_USERS,
	onAvatarClick,
	size = 'lg',
	users,
	recipients,
	...rest
}) => {
	const usersRecipients = users.map((user) => new Recipient(user));
	const { head, tail, doesOverflow, overflowCount } = splitOverflow(recipients || usersRecipients, { max, minOverflow: 2 });

	const overflowUserNames = useMemo(() => tail.map(pluck('displayName')), [tail]);

	return (
		<Flex dir="row" alignItems="center" {...rest}>
			{head.map((recipient) => {
				const isUser = recipient.type !== 'group';

				return (
					<RecipientPopover key={recipient.id} recipient={recipient} isDisabled={isPopoverDisabled}>
						{isUser ? (
							<ProfileLink userId={recipient.id} lineHeight={0} display="flex" alignItems="center" marginInlineEnd={rem(-8)}>
								<Avatar name={recipient.displayName} src={recipient.profilePic} onClick={onAvatarClick} size={sizeMap[size]} />
							</ProfileLink>
						) : (
							<AvatarTriggerWrapper aria-label={recipient.displayName} {...getMarginProps()}>
								{isGroupAvatarType(recipient.dimension) ? (
									<GroupAvatar groupType={recipient.dimension} size={sizeMap[size]} onClick={onAvatarClick} />
								) : (
									<Avatar name={recipient.displayName} src={recipient.profilePic} onClick={onAvatarClick} size={sizeMap[size]} />
								)}
							</AvatarTriggerWrapper>
						)}
					</RecipientPopover>
				);
			})}

			{doesOverflow && <UserAvatarExcess count={overflowCount} userNames={overflowUserNames} size={sizeMap[size]} {...getMarginProps()} />}
		</Flex>
	);
};
